// The schemas are a set of types, each associated with a set of properties.
// Schema are used to improve the way search engines read and represent our page.
// We use Schema in the JSON+LD format.
// You can read more at https://schema.org

import { PDPPageReview } from 'utils/types';

export type ProductDataSchema = {
  productId: number;
  name: string;
  slug: string;
  sku: string;
  upc: string;
  price: string;
  powerReviews: {
    average_rating: number;
    review_count: number;
    rating_histogram: number[];
    recommended_ratio: number;
    contents: PDPPageReview[];
  };
  outOfStock: boolean;
  promotionable: boolean;
};

interface questionJSONLDSchema {
  '@type': SchemaType;
  name: string;
  acceptedAnswer: {
    '@type': SchemaType;
    text: string;
  };
}

interface ListItemJSONLDSchema {
  '@type': SchemaType;
  position: number;
  url: string;
}

export enum SchemaType {
  ORGANIZATION = 'Organization',
  FAQ_PAGE = 'FAQPage',
  PRODUCT = 'Product',
  PRODUCT_GROUP = 'ProductGroup',
  OFFER = 'Offer',
  LIST_ITEM = 'ListItem',
  BRAND = 'Brand',
  AGGREGATE_RATING = 'AggregateRating',
  ITEM_LIST = 'ItemList',
  QUESTION = 'Question',
  ANSWER = 'Answer',
  PERSON = 'Person',
  REVIEW = 'Review',
  RATING = 'Rating',
  LOCATION_CREATED = 'AdministrativeArea',
  POSTAL_ADDRESS = 'PostalAddress',
  CONTACT_POINT = 'ContactPoint',
  GEO_COORDINATES = 'GeoCoordinates',
  OPENING_HOURS_SPECIFICATION = 'OpeningHoursSpecification',
  MEDICAL_CLINIC = 'MedicalClinic',
  OFFER_SHIPPING_DETAILS = 'OfferShippingDetails',
  DEFINED_REGION = 'DefinedRegion',
  MONETARY_AMOUNT = 'MonetaryAmount',
  MERCHANT_RETURN_POLICY = 'MerchantReturnPolicy',
  ARTICLE = 'Article',
  BLOG_POSTING = 'BlogPosting',
  IMAGE_OBJECT = 'ImageObject',
  WEB_PAGE = 'WebPage',
  HOW_TO = 'HowTo',
  HOW_TO_STEP = 'HowToStep',
  BREADCRUMB_LIST = 'BreadcrumbList',
}

export enum Availability {
  OUT_OF_STOCK = 'https://schema.org/OutOfStock',
  IN_STOCK = 'https://schema.org/InStock',
}

/**
 * Structured Data for Reviews
 * This schema represents a review from an user.
 */
export interface ReviewJSONLDSchema {
  '@type': SchemaType;
  name: string;
  reviewBody: string;
  datePublished: string;
  author: {
    '@type': SchemaType;
    name: string;
  };
  locationCreated: {
    '@type': SchemaType;
    name: string;
  };
  reviewRating: {
    '@type': SchemaType;
    ratingValue: number;
  };
}

/**
 * Structured data required by crawlers to match
 * data in Google Merchant feed. Please note that
 * SKU in this context is meant to match productId
 * rather than our internal use of "sku"; gtin12 is
 * a 12 character string representing our product
 * UPC codes.
 */
export interface ProductJSONLDSchema {
  '@context'?: string;
  '@type': SchemaType;
  name: string;
  brand: {
    '@type': SchemaType;
    name: string;
  };
  description: string;
  sku: string;
  gtin12: string | null;
  url: string;
  image: string | undefined;
  aggregateRating?: {
    '@type': SchemaType;
    ratingValue?: number;
    reviewCount?: number;
  };
  review: ReviewJSONLDSchema[];
  offers: {
    '@type': SchemaType;
    sku: string;
    url: string;
    priceValidUntil: string;
    availability: Availability;
    price: string;
    priceCurrency: string;
    shippingDetails?: OfferShippingDetails;
    hasMerchantReturnPolicy?: MerchantReturnPolicy;
  };
  category?: string;
  isRelatedTo?: ProductReference | ProductReference[];
  /** Present when this Product is nested under a ProductGroup.hasVariant. */
  inProductGroupWithID?: string;
}

/**
 * Nested ProductGroup schema (Google product variants).
 * `variesBy` is intentionally omitted for FS/FSC — panel scope is not one of
 * Google's six allowed values (IN2-4999 / IN2-4998).
 * @see https://developers.google.com/search/docs/appearance/structured-data/product-variants
 */
export interface ProductGroupJSONLDSchema {
  '@context': string;
  '@type': SchemaType;
  name: string;
  description: string;
  url: string;
  brand: {
    '@type': SchemaType;
    name: string;
  };
  productGroupID: string;
  hasVariant: ProductJSONLDSchema[];
}

/**
 * Structured data in format recommended by Google for
 * summary listing pages (used in our case for the PIP)
 * https://developers.google.com/search/docs/advanced/structured-data/carousel#summary-page
 */
export interface ItemListJSONLDSchema {
  '@context': string;
  '@type': SchemaType;
  name: string;
  itemListElement: ListItemJSONLDSchema[];
}

export interface FaqJSONLDSchema {
  '@context': string;
  '@type': SchemaType;
  mainEntity: questionJSONLDSchema[];
}

export interface OpeningHoursSpecification {
  '@type': SchemaType;
  dayOfWeek: string | string[];
  opens: string | null;
  closes: string | null;
}

export interface OfferShippingDetails {
  '@type': SchemaType;
  shippingDestination: {
    '@type': SchemaType;
    addressCountry: string;
  };
  shippingRate: {
    '@type': SchemaType;
    value: string;
    currency: string;
  };
}

export interface MerchantReturnPolicy {
  '@type': SchemaType;
  applicableCountry: string;
  returnPolicyCategory: string;
  merchantReturnDays: number;
}

export interface ProductReference {
  '@type': SchemaType;
  '@id': string;
}

export interface OrganizationJSONLDSchema {
  '@context': string;
  '@type': SchemaType;
  name: string;
  legalName: string;
  url: string;
  logo: string;
  foundingDate: string;
  founders: {
    '@type': SchemaType;
    name: string;
  };
  address: {
    '@type': SchemaType;
    streetAddress: string;
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    addressCountry: string;
  };
  geo?: {
    '@type': SchemaType;
    latitude: number;
    longitude: number;
  };
  contactPoint: {
    '@type': SchemaType;
    contactType: string;
    email: string;
  };
  sameAs: string[];
  openingHoursSpecification?: OpeningHoursSpecification[];
}

export interface ArticleJSONLDSchema {
  '@context': string;
  '@type': SchemaType;
  headline: string;
  description: string;
  image?: string;
  datePublished: string;
  dateModified?: string;
  author: {
    '@type': SchemaType;
    name: string;
    url?: string;
  };
  publisher: {
    '@type': SchemaType;
    name: string;
    logo?: {
      '@type': SchemaType;
      url: string;
    };
  };
  mainEntityOfPage: {
    '@type': SchemaType;
    '@id': string;
  };
}

export interface HowToStepJSONLDSchema {
  '@type': SchemaType;
  name?: string;
  text: string;
  position: number;
}

export interface HowToJSONLDSchema {
  '@context': string;
  '@type': SchemaType;
  name: string;
  description?: string;
  step: HowToStepJSONLDSchema[];
}

export interface BreadcrumbItemJSONLDSchema {
  '@type': SchemaType;
  position: number;
  name: string;
  item?: string;
}

export interface BreadcrumbListJSONLDSchema {
  '@context': string;
  '@type': SchemaType;
  itemListElement: BreadcrumbItemJSONLDSchema[];
}

export type JSONLDSchema =
  | ProductJSONLDSchema
  | ProductGroupJSONLDSchema
  | ItemListJSONLDSchema
  | FaqJSONLDSchema
  | OrganizationJSONLDSchema
  | ArticleJSONLDSchema
  | HowToJSONLDSchema
  | BreadcrumbListJSONLDSchema;
